import { Box, InputBase } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdSearch } from 'react-icons/md'
import { invalidateProductCache, useAddProduct } from '../../../api/product'
import { MainButton } from '../../../components/MainButton'
import { ScreenTitle } from '../../../components/ScreenTitle'
import { ShowDialogProps } from '../../../show-dialog'
import { ProductForm } from './ProductForm'
import { ProductTable } from './ProductTable'
import { invalidatePositionDrawingCache } from '../../../api/position-drawing'
import { useDialogCtx } from '../../../hooks/context-hooks'
import { toast } from 'react-toastify'
import { ProductFilters } from 'src/generated-types'

interface CreateProductProps extends ShowDialogProps {
  positionDrawingId?: EntityId
  positionDrawingNumber?: string
}

export const CreateProductDialog: React.FC<CreateProductProps> = ({
  positionDrawingId,
  positionDrawingNumber,
  pop,
}) => {
  const mutation = useAddProduct()
  const { t } = useTranslation(['common-api-message'])

  const onSubmit = async form => {
    await mutation.mutateAsync(form, {
      onSuccess: () => {
        toast.success(t('SuccessfullyCreated', { ns: 'common-api-message' }))
        invalidateProductCache.getProducts()
        positionDrawingId && invalidatePositionDrawingCache.getPositionDrawingById(positionDrawingId)
        pop(true)
      },
      onError: () => {
        toast.error(t('InternalError', { ns: 'common-api-message' }))
      },
    })
  }

  return (
    <ProductForm
      onSubmit={onSubmit}
      onCancel={() => pop()}
      initialValues={{ positionDrawingId: positionDrawingId, positionDrawingNumber: positionDrawingNumber }}
    />
  )
}

export const ProductContainer = () => {
  const { t: translate } = useTranslation(['component', 'common'])
  const { showDialog } = useDialogCtx()

  const [searchInput, setSearchInput] = useState<string>('')
  const [headerFilters, setHeaderFilters] = useState<ProductFilters>({})

  const onAdd = async () => {
    const added = await showDialog(CreateProductDialog, {
      title: translate('addProduct', { ns: 'product' }),
      opts: {
        maxWidth: 'md',
        fullWidth: true,
      },
      componentProps: {},
    })
    if (!added) return
  }

  const onSearchInputChange = (searchInput: string) => {
    setSearchInput(searchInput)
  }

  return (
    <>
      <Box className="p-3 mb-2 bg-white shadow-md" borderRadius={2}>
        <Box className="flex flex-wrap justify-between mb-2">
          <ScreenTitle title={translate('products', { ns: 'product' })} />
          <Box className="flex flex-wrap-reverse items-center justify-end">
            <Box
              className="flex items-center gap-3 mr-2 w-30 md:w-40 lg:w-80"
              sx={{ borderRadius: 2, borderColor: 'gray', borderWidth: '1px' }}
            >
              <Box className="ml-3">
                <MdSearch color="gray" size={20} />
              </Box>
              <InputBase
                sx={{ borderColor: 'white' }}
                autoFocus={searchInput != '' ? true : false}
                placeholder={translate('search', { ns: 'common' }) + '...'}
                inputProps={{ 'aria-label': 'search' }}
                defaultValue={searchInput}
                onChange={event => onSearchInputChange(event.target.value)}
              />
            </Box>
            <Box>
              <MainButton onClick={onAdd}>{translate('add', { ns: 'common' })}</MainButton>
            </Box>
          </Box>
        </Box>
        <ProductTable searchValue={searchInput} headerFilters={headerFilters} setHeaderFilters={setHeaderFilters} />
      </Box>
    </>
  )
}
